import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core'
import type { PdfDokument, PdfLink, PdfTextebene, PdfZeichenAuftrag } from './pdf-dokument'
import { linkAnsicht, ueberlappt, type PdfLinkAnsicht } from './pdf-links'

/** So lange wird nach einer Zoom-Aenderung gewartet, bevor neu gezeichnet wird (Mausrad, Plus-Knopf). */
const ZEICHNEN_NACH_ZOOM_MS = 120

/**
 * Eine Seite in `lib-pdf`: die Leinwand, darueber die unsichtbare Textebene, die Markieren,
 * Kopieren und die Suche des Browsers (Strg+F) traegt, und ganz oben die Links.
 *
 * Gezeichnet wird nur, solange die Seite in der Naehe des sichtbaren Bereichs liegt; weiter weg wird
 * die Leinwand freigegeben, sonst haelt ein Sammel-PDF mit 40 Seiten bei 200 % mehrere hundert MB.
 * Die Textebene entsteht dagegen fuer jede Seite sofort, sonst faende Strg+F nur, was schon einmal
 * im Bild war.
 *
 * Links kommen aus zwei Quellen: den Link-Anmerkungen der Datei (hochgeladene Schreiben, Vertraege)
 * und Adressen, die nur als Text dastehen (unsere eigenen Vorlagen). Die zweite Sorte hat der Viewer
 * des Browsers frueher selbst verlinkt; ohne sie liess sich nach dem Umbau nichts mehr anklicken
 * (Befund Max 2026-10-29). Beide stehen in Prozent der Seite, ein Zoom misst also nichts neu.
 *
 * Ohne Kapselung: pdf.js legt die Spannen der Textebene selbst an, die Komponentenstile erreichten
 * sie sonst nicht. Jede Regel haengt deshalb unter `.lib-pdf-seite`.
 */
@Component({
  selector: 'lib-pdf-seite',
  template: `
    <div #textebene class="textLayer"></div>
    @if (links().length) {
      <div class="linkebene">
        @for (link of links(); track $index) {
          <a
            class="lib-pdf-link"
            [href]="link.href"
            [attr.target]="link.neuesFenster ? '_blank' : null"
            rel="noopener noreferrer"
            [attr.aria-label]="link.beschriftung"
            [attr.title]="link.seite ? link.beschriftung : null"
            [style.left.%]="link.links"
            [style.top.%]="link.oben"
            [style.width.%]="link.breite"
            [style.height.%]="link.hoehe"
            (click)="folge($event, link)"
          ></a>
        }
      </div>
    }
    @if (fehler()) {
      <p class="seitenfehler mat-body-small">Diese Seite lässt sich nicht anzeigen.</p>
    }
  `,
  styleUrl: './pdf-seite.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'lib-pdf-seite',
    role: 'group',
    '[attr.aria-label]': '"Seite " + nummer()',
    '[style.width.px]': 'breite()',
    '[style.height.px]': 'hoehe()',
    '[style.--total-scale-factor]': 'skala()',
  },
})
export class PdfSeiteComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef)

  readonly dokument = input.required<PdfDokument>()
  readonly nummer = input.required<number>()
  /** CSS-Pixel je PDF-Punkt. */
  readonly skala = input.required<number>()
  readonly drehung = input.required<number>()
  readonly breite = input.required<number>()
  readonly hoehe = input.required<number>()
  /** Der Scroll-Container, gegen den die Sichtbarkeit gemessen wird. */
  readonly wurzel = input.required<HTMLElement>()

  /** Ein Link im Dokument auf eine andere Seite (ab 1), etwa aus einem Inhaltsverzeichnis. */
  readonly springeZu = output<number>()

  protected readonly fehler = signal(false)

  private readonly textContainer = viewChild.required<ElementRef<HTMLElement>>('textebene')
  private readonly sichtbar = signal(false)

  private readonly dateiLinks = signal<readonly PdfLink[]>([])
  /** `undefined`, solange die Textebene noch nicht vermessen ist. */
  private readonly textLinks = signal<readonly PdfLink[] | undefined>(undefined)
  private readonly textBereit = signal(false)

  /** Eine Adresse im Text, die die Datei schon selbst verlinkt, bekommt keinen zweiten Link. */
  protected readonly links = computed<PdfLinkAnsicht[]>(() => {
    const ausDatei = this.dateiLinks()
    const ausText = (this.textLinks() ?? []).filter((link) => !ausDatei.some((vorhanden) => ueberlappt(vorhanden, link)))
    const drehung = this.drehung()
    return [...ausDatei, ...ausText].map((link) => linkAnsicht(link, drehung))
  })

  private auftrag?: PdfZeichenAuftrag
  private leinwand?: HTMLCanvasElement
  private textebene?: PdfTextebene
  private textebeneFertig = false

  constructor() {
    const destroyRef = inject(DestroyRef)

    afterNextRender(() => {
      // Vorlauf von einer halben Flaechenhoehe: die Seite ist gezeichnet, bevor sie ins Bild rollt.
      const beobachter = new IntersectionObserver(([eintrag]) => this.sichtbar.set(eintrag.isIntersecting), {
        root: this.wurzel(),
        rootMargin: '50% 0px',
      })
      beobachter.observe(this.host.nativeElement)
      destroyRef.onDestroy(() => beobachter.disconnect())
    })

    effect((onCleanup) => {
      const dokument = this.dokument()
      const nummer = this.nummer()
      const skala = this.skala()
      const drehung = this.drehung()
      if (!this.sichtbar()) {
        this.leinwandFreigeben()
        return
      }
      const warten = this.leinwand ? ZEICHNEN_NACH_ZOOM_MS : 0
      const zeitgeber = setTimeout(() => void this.zeichne(dokument, nummer, skala, drehung), warten)
      onCleanup(() => clearTimeout(zeitgeber))
    })

    effect((onCleanup) => {
      const dokument = this.dokument()
      const nummer = this.nummer()
      const container = this.textContainer().nativeElement
      const ebene = untracked(() => dokument.textebene(nummer, container, this.skala(), this.drehung()))
      this.textebene = ebene
      this.textebeneFertig = false
      this.textBereit.set(false)
      this.textLinks.set(undefined)
      void ebene.fertig.then(() => {
        if (this.textebene === ebene) {
          this.textebeneFertig = true
          // Wurde waehrenddessen gezoomt, zieht die Ebene jetzt nach.
          ebene.aktualisiere(this.skala(), this.drehung())
          this.textBereit.set(true)
        }
      })
      onCleanup(() => ebene.abbrechen())
    })

    effect(() => {
      const skala = this.skala()
      const drehung = this.drehung()
      if (this.textebeneFertig) {
        this.textebene?.aktualisiere(skala, drehung)
      }
    })

    effect((onCleanup) => {
      const dokument = this.dokument()
      const nummer = this.nummer()
      let gilt = true
      this.dateiLinks.set([])
      void dokument.links(nummer).then(
        (links) => {
          if (gilt) {
            this.dateiLinks.set(links)
          }
        },
        () => undefined,
      )
      onCleanup(() => (gilt = false))
    })

    // Erst messen, wenn die Seite in der Naehe des Sichtbaren liegt: in einem verdeckten Kasten hat
    // die Textebene noch keine Groesse.
    afterRenderEffect({
      read: () => {
        if (!this.sichtbar() || !this.textBereit() || untracked(this.textLinks) !== undefined) {
          return
        }
        const rahmen = this.host.nativeElement.getBoundingClientRect()
        const ebene = this.textebene
        if (ebene && rahmen.width > 0 && rahmen.height > 0) {
          this.textLinks.set(ebene.adressen(rahmen, untracked(this.drehung)))
        }
      },
    })

    destroyRef.onDestroy(() => this.leinwandFreigeben())
  }

  protected folge(ereignis: MouseEvent, link: PdfLinkAnsicht) {
    if (link.seite) {
      ereignis.preventDefault()
      this.springeZu.emit(link.seite)
    }
  }

  private async zeichne(dokument: PdfDokument, nummer: number, skala: number, drehung: number) {
    this.auftrag?.abbrechen()
    const auftrag = dokument.zeichne(nummer, skala, drehung)
    this.auftrag = auftrag
    try {
      const neu = await auftrag.fertig
      if (!neu || this.auftrag !== auftrag) {
        return
      }
      // Die alte Leinwand bleibt stehen, bis die neue fertig ist: beim Zoomen gibt es kein weisses Aufblitzen.
      this.leinwand?.remove()
      this.leinwand = neu
      this.host.nativeElement.prepend(neu)
      this.fehler.set(false)
    } catch {
      if (this.auftrag === auftrag) {
        this.fehler.set(true)
      }
    }
  }

  private leinwandFreigeben() {
    this.auftrag?.abbrechen()
    this.auftrag = undefined
    if (this.leinwand) {
      // Breite 0 gibt den Speicher sofort frei; Safari haelt ihn sonst bis zur naechsten Bereinigung.
      this.leinwand.width = 0
      this.leinwand.height = 0
      this.leinwand.remove()
      this.leinwand = undefined
    }
  }
}
